import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ArrowDown } from 'lucide-react';
import { SuccessStories } from './SuccessStories';

/** Where on screen a day counts as "reached". 0.62 = a little below centre. */
const ANCHOR = 0.62;

/** How far into a card the "reached" point sits, in px from its top edge. */
const DAY_ANCHOR_OFFSET = 28;

/**
 * Turn whatever the workshop has into day cards.
 *
 * A workshop edited in the admin console has a real `curriculum`. One that has
 * not been filled in yet still has `highlights`, and showing those as a single
 * module beats showing an empty section on a page whose only job is to sell
 * this one workshop.
 */
function toDays(workshop) {
  if (Array.isArray(workshop.curriculum) && workshop.curriculum.length > 0) {
    return workshop.curriculum;
  }

  const highlights = Array.isArray(workshop.highlights) ? workshop.highlights : [];
  if (highlights.length === 0) return [];

  return [
    {
      id: 'overview',
      label: 'The workshop',
      title: "What you'll cover",
      subtitle: workshop.outcome ? `You leave able to: ${workshop.outcome}.` : '',
      lessons: highlights.map((text) => ({ title: text, body: '' })),
    },
  ];
}

/**
 * The curriculum, day by day.
 *
 * The cards run the full width of the column — on a phone every column given
 * to a rail or a marker is taken off the copy, which is what the section is
 * actually selling. Where the reader is up to is still tracked, and still
 * shown, but on the cards themselves: the day they are level with says so, and
 * the ones behind them are ticked off.
 *
 * The student reviews used to open each card, one per day. They have their own
 * section now — see `SuccessStories`, rendered above the days — because one
 * review per card meant a visitor only ever met whichever one they happened to
 * be level with, and no player could hand on to the next.
 */
export const CurriculumScroll = ({ workshop }) => {
  const days = useMemo(() => toDays(workshop), [workshop]);

  const listRef = useRef(null);
  const dayRefs = useRef([]);
  const frame = useRef(0);

  const [done, setDone] = useState(0);

  const measure = useCallback(() => {
    if (!listRef.current) return;

    const anchor = window.innerHeight * ANCHOR;

    // A card with no box has not been laid out yet. Its rect reads as all
    // zeros, which would count every day as cleared before the section has
    // even been scrolled to.
    const marks = dayRefs.current
      .filter(Boolean)
      .map((node) => node.getBoundingClientRect())
      .filter((box) => box.height > 0)
      .map((box) => box.top + DAY_ANCHOR_OFFSET);

    if (marks.length === 0) return;

    // Most scroll frames move nothing here — bail out rather than re-rendering
    // the whole syllabus on every one of them.
    setDone(marks.filter((mark) => mark <= anchor).length);
  }, []);

  useEffect(() => {
    // Scroll fires far more often than the screen repaints; coalesce to a frame.
    const onScroll = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Layout changes go straight to measure() rather than through the frame
    // above. The first measurement necessarily runs before the images and web
    // fonts have settled, and each of those moves the day cards — but a
    // curriculum short enough to sit on one screen never fires a scroll event
    // to correct itself, so it would keep its mount-time geometry for good.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (observer && listRef.current) observer.observe(listRef.current);
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [measure, days.length]);

  if (days.length === 0) return null;

  return (
    <>
      {/* The proof, before the promise. Its own section rather than the head of
          a card, so all of it is met in one place. */}
      <SuccessStories />

      <section id="curriculum" className="section bg-[#fffdfb] text-ink-900">
        <div className="shell">
          <span className="pill pill-brand">Day by day</span>
          <h2 className="mt-3.5 font-display text-display-md font-bold">
            What You’ll Learn{' '}
            <span className="font-display-italic text-brand-700">Each Day</span>
          </h2>
          <p className="mt-2.5 text-sm text-ink-500 leading-relaxed">
            Follow our {days.length}-day journey from breaking the fear of speaking to
            speaking with confidence.
          </p>
        </div>

        {/* ── The days ──────────────────────────────────────────────────────── */}
        <div ref={listRef} className="shell mt-7">
          <ol className="space-y-4">
            {days.map((day, i) => {
              // Three states, not two. "Current" is the day the reader is level
              // with — without it the list only ever says done or not-yet, and
              // nothing on screen marks where they actually are.
              const state = i < done ? 'done' : i === done ? 'current' : 'todo';
              const lessons = Array.isArray(day.lessons) ? day.lessons : [];

              return (
                <li
                  key={day.id || i}
                  ref={(node) => {
                    dayRefs.current[i] = node;
                  }}
                  data-state={state}
                >
                  {/* Card. Full width of the column — on a phone every column
                      spent on a gutter is one taken off the copy. */}
                  <div className="day-card relative rounded-3xl border p-5 overflow-hidden">
                    {/* Accent stripe. Grows down the card's edge as the day is
                        cleared — the last of the old rail, kept because it is
                        the one part of it that costs no width. */}
                    <span className="day-accent" aria-hidden="true" />

                    <div className="flex items-center justify-between gap-3">
                      <span className="day-label text-[10px] font-bold uppercase tracking-[0.14em]">
                        {day.label || `Day ${i + 1}`}
                      </span>

                      {state === 'done' && (
                        <span className="pill pill-emerald !px-2 !py-0.5 !text-[9px] animate-pop-in">
                          Covered
                        </span>
                      )}
                      {state === 'current' && (
                        <span className="pill pill-brand !px-2 !py-0.5 !text-[9px] animate-pop-in">
                          You are here
                        </span>
                      )}
                    </div>

                    {/* Sans, not the Playfair used by the section heading above.
                        Set at display sizes the serif competes with the section
                        title for the same job; the sans reads as a rung down. */}
                    <h3 className="day-title mt-2 font-sans text-[1.18rem] font-extrabold tracking-[-0.015em] leading-snug">
                      {day.title}
                    </h3>

                    {day.subtitle && (
                      <p className="mt-2 text-[0.8rem] text-ink-500 leading-relaxed">
                        {day.subtitle}
                      </p>
                    )}

                    {lessons.length > 0 && (
                      <ul className="mt-4 space-y-3">
                        {lessons.map((lesson, index) => (
                          <li key={lesson.title || index} className="flex gap-2.5">
                            {/* Staggered, so clearing a day ripples down its
                                lessons instead of flipping them all at once. */}
                            <span
                              className="day-bullet mt-[0.42rem] w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ transitionDelay: `${index * 45}ms` }}
                            />
                            <div className="min-w-0">
                              <p className="text-[0.82rem] font-semibold text-ink-800 leading-snug">
                                {lesson.title}
                              </p>
                              {lesson.body && (
                                <p className="text-[0.75rem] text-ink-500 leading-relaxed mt-0.5">
                                  {lesson.body}
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <p className="shell mt-8 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-400">
          Book your seat below
          <ArrowDown className="w-4 h-4" />
        </p>
      </section>
    </>
  );
};
